import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';

import { useAuth } from '../context/AuthContext';
import { useQueue } from '../context/QueueContext';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSpinner, FaClock, FaExpandArrowsAlt, FaTimes } from 'react-icons/fa';
import BackButton from '../components/BackButton';
import { ArrowLeft } from 'lucide-react';
import { API_ENDPOINTS } from '../config/backendConfig';
import { saveActivity, ACTIVITY_TYPES, checkExistingActivity } from '../utils/activityService';
import { resolveImageUrl } from '../utils/urlResolver';
import KidBackground from '../ui/backgrounds/KidBackground';
import splashImage from '../assets/splash.png';

const AIMagicPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { imageUrl, title, technique, inspiration, userArtworks: passedUserArtworks, fromMyArtWorld, selectedFeature: passedSelectedFeature, artwork } = location.state || {};
  const { user } = useAuth();
  const isAuthenticated = !!user;
  
  // Check if accessed from mentor dashboard
  const isFromMentorDashboard = location.state?.fromMentorDashboard || 
                               location.search.includes('mentorMode=true') || 
                               user?.accountType === 'mentor';
  
  // Check for mentor-selected artwork
  const [mentorArtwork, setMentorArtwork] = useState(null);
  
  // State for user artworks carousel (for non-mentor users)
  const [userArtworks, setUserArtworks] = useState(passedUserArtworks || []);
  const [showArtworkCarousel, setShowArtworkCarousel] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  
  const { 
    generateAppreciation, 
    generateScene, 
    askMentor, 
    activeJobs, 
    hasActiveJobs,
    queueStats 
  } = useQueue();
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [aiPoints, setAiPoints] = useState([]);
  const [concepts, setConcepts] = useState([]); // dynamic "Did You Know" concepts tied to the analysed image
  const [suggestion, setSuggestion] = useState("");
  const [techniques, setTechniques] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visibleBoxIndex, setVisibleBoxIndex] = useState(null);
  const [showTipBox, setShowTipBox] = useState(false);
  const [showTechniques, setShowTechniques] = useState(false);
  const [tips, setTips] = useState([]);
  const [artAnalysis, setArtAnalysis] = useState(null);
  const [techniquesLoading, setTechniquesLoading] = useState(false);
  const [techniquesInitialized, setTechniquesInitialized] = useState(false);
  const [storyLoading, setStoryLoading] = useState(false);
  const [error, setError] = useState(null);

  const [userQuery, setUserQuery] = useState("");
  const [mentorResponse, setMentorResponse] = useState(null);

  const imageRef = useRef(null);

  const [selectedTechnique, setSelectedTechnique] = useState(null);
  const [showTechniquePopup, setShowTechniquePopup] = useState(false);

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showLocationPopup, setShowLocationPopup] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Determine the correct image source - prioritize artwork object from My Art World
  const artworkImageUrl = artwork?.imageUrl || artwork?.artworkUrl;
  const artworkTitle = artwork?.title || artwork?.artworkTitle;
  const currentImageUrl = artworkImageUrl || mentorArtwork?.file || imageUrl;
  const currentTitle = artworkTitle || mentorArtwork?.name || title;
  const currentArtist = mentorArtwork?.artist || 'Unknown Artist';

  // Debug: Log the received imageUrl
  console.log('AIMagicPage - Received imageUrl:', imageUrl);
  console.log('AIMagicPage - Location state:', location.state);
  console.log('AIMagicPage - Mentor artwork:', mentorArtwork);
  console.log('AIMagicPage - Selected feature:', selectedFeature);
  console.log('AIMagicPage - User account type:', user?.accountType);
  console.log('AIMagicPage - Current image URL:', currentImageUrl);
  console.log('AIMagicPage - Current title:', currentTitle);

  const reimagineFeatures = [
    {
      title: 'Add Magic Changes',
      desc: 'Get fun ideas to make your art even cooler!'
    },
    {
      title: 'Try Art Styles',
      desc: 'See your art in famous styles like Impressionism or Cubism!'
    },
    {
      title: 'Chat with Artmosphere',
      desc: 'Tell Artmosphere what you want to change in your art!'
    }
  ];

  // Add CSS animation for gradient shift
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes gradientShift {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const fetchAppreciation = async (imageUrl, title, technique, inspiration) => {
    if (!imageUrl || !title) {
      setError("Please provide both an image and a title.");
      return;
    }

    // Prevent duplicate calls
    if (loading) {
      console.log("Appreciation fetch already in progress, skipping...");
      return;
    }

    setLoading(true);
    setError(null);
    console.log("Starting appreciation fetch with:", { imageUrl, title, technique, inspiration });

    try {
      // Prepare an image hash for large/base64 inputs to use in dedup/caching
      let imageHash = null;
      try {
        const str = imageUrl || '';
        if (str.length > 300) {
          let h = 0;
          for (let i = 0; i < str.length; i++) {
            h = (h * 31 + str.charCodeAt(i)) >>> 0;
          }
          imageHash = h.toString(16);
        }
      } catch (_) {}

      // 1) For mentors: check cached activity first (no re-trigger for same artwork)
      if (user?.accountType === 'mentor') {
        try {
          const cached = await checkExistingActivity(imageUrl, ACTIVITY_TYPES.APPRECIATION || 'appreciation');
          if (cached && Array.isArray(cached.aiPoints) && cached.aiPoints.length > 0) {
            console.log('✅ Found cached mentor appreciation. Skipping API and navigating to feedback.');
            const persisted = {
              imageUrl,
              title,
              technique,
              inspiration,
              appreciation: cached.aiPoints,
              activity: cached.activity || null,
              artAnalysis: cached.artAnalysis || null,
              techniques: cached.techniques || [],
              concepts: cached.concepts || []
            };
            try { sessionStorage.setItem('artmosphere_last_appreciation', JSON.stringify(persisted)); } catch(_) {}
            navigate('/art-appreciation/feedback', { state: persisted });
            return;
          }
        } catch (e) {
          console.warn('Cache lookup failed, proceeding to generation:', e?.message || e);
        }
      }

      // Convert blob URL to base64 data URL if needed
      let processedImageUrl = imageUrl;
      if (imageUrl.startsWith('blob:')) {
        console.log("Converting blob URL to base64...");
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        processedImageUrl = base64;
        console.log("Converted to base64 data URL");
      }

      console.log("Making API call to:", `${API_ENDPOINTS.backendBase}/api/artwork-appreciation`);
      const response = await fetch(`${API_ENDPOINTS.backendBase}/api/artwork-appreciation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          artwork: {
            imageUrl: processedImageUrl,
            title,
            artistName: mentorArtwork?.artist || 'Unknown Artist',
            artMovement: 'Contemporary Art'
          },
          skipCache: true // Always generate fresh analysis for mentor uploads
        }),
      });

      console.log("API response status:", response.status);
      console.log("API response ok:", response.ok);

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Appreciation API error:", errorData);
        console.error("Full error response:", {
          status: response.status,
          statusText: response.statusText,
          errorData: errorData
        });
        throw new Error(errorData.error || 'Failed to generate appreciation');
      }

      const data = await response.json();
      console.log("✅ Received appreciation data:", data);
      console.log("Response structure check:", {
        hasPoints: !!data.points,
        pointsIsArray: Array.isArray(data.points),
        pointsLength: data.points?.length,
        hasSuggestion: !!data.suggestion,
        hasArtAnalysis: !!data.artAnalysis,
        hasTechniques: !!data.techniques,
        fullDataKeys: Object.keys(data)
      });

      if (!data.appreciation || !Array.isArray(data.appreciation)) {
        console.error("Invalid response structure:", data);
        throw new Error(`Invalid response format: missing appreciation array. Response keys: ${Object.keys(data).join(', ')}`);
      }

      setAiPoints(data.appreciation);
      console.log("✅ Set aiPoints:", data.appreciation);
      setSuggestion(data.activity?.objective || '');
      if (Array.isArray(data.concepts)) {
        setConcepts(data.concepts);
      } else {
        setConcepts([]);
      }
      
      // Fix: Extract techniques from techniqueImprove structure and deduplicate
      const techniquesFromArtAnalysis = data.artAnalysis?.techniques || [];
      const techniquesFromRoot = data.techniqueImprove || [];
      
      // Create a Map to deduplicate techniques by name
      const techniqueMap = new Map();
      
      // Helper function to normalize technique names for comparison
      const normalizeName = (name) => {
        return name.toLowerCase().trim().replace(/[^\w\s]/g, '');
      };
      
      // Add techniques from artAnalysis first (they take priority)
      techniquesFromArtAnalysis.forEach(technique => {
        if (technique.name) {
          const normalizedName = normalizeName(technique.name);
          techniqueMap.set(normalizedName, technique);
        }
      });
      
      // Add techniques from techniqueImprove (these are strings, convert to objects)
      techniquesFromRoot.forEach((techniqueText, index) => {
        if (typeof techniqueText === 'string') {
          const technique = {
            name: `Technique ${index + 1}`,
            description: techniqueText,
            confidence: 0.8
          };
          const normalizedName = normalizeName(technique.name);
          if (!techniqueMap.has(normalizedName)) {
            techniqueMap.set(normalizedName, technique);
          }
        } else if (techniqueText.name) {
          const normalizedName = normalizeName(techniqueText.name);
          if (!techniqueMap.has(normalizedName)) {
            techniqueMap.set(normalizedName, techniqueText);
          } else {
            console.log(`Duplicate technique found: "${techniqueText.name}" - skipping`);
          }
        }
      });
      
      // Convert Map back to array
      const allTechniques = Array.from(techniqueMap.values());
      
      setTechniques(allTechniques);
      setArtAnalysis(data.artAnalysis || null);
      setTechniquesInitialized(true);
      
      // For mentors, redirect to the appreciation results page after successful processing
      if (user?.accountType === 'mentor') {
        console.log("✅ Appreciation generated successfully, redirecting to results page...");
        try {
          const persisted = {
            imageUrl: processedImageUrl,
            title,
            technique,
            inspiration,
            appreciation: data.points || data.appreciation,
            activity: data.activity,
            artAnalysis: data.artAnalysis,
            techniques: allTechniques,
            concepts: data.concepts || []
          };
          sessionStorage.setItem('artmosphere_last_appreciation', JSON.stringify(persisted));
        } catch (_) {}
        navigate('/art-appreciation/feedback', {
          state: { 
            imageUrl: processedImageUrl, 
            title, 
            technique, 
            inspiration,
            appreciation: data.points || data.appreciation, // send structured points with improve arrays
            activity: data.activity,
            artAnalysis: data.artAnalysis,
            techniques: allTechniques,
            concepts: data.concepts || []
          }
        });
        return; // Exit early to prevent setting loading to false
      }
    } catch (error) {
      console.error("Appreciation error:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Load user artworks for carousel (define before useEffect to avoid TDZ)
  const loadUserArtworks = async () => {
    if (!user || user.accountType === 'mentor') return;
    
    try {
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      
      const [artworksQuery, sharedArtworksQuery] = await Promise.all([
        getDocs(query(collection(db, 'artworks'), where('uid', '==', user.uid))),
        getDocs(query(collection(db, 'sharedArtworks'), where('uid', '==', user.uid)))
      ]);
      
      const artworksData = [
        ...artworksQuery.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          source: 'artworks'
        })),
        ...sharedArtworksQuery.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.artworkTitle || data.title,
            imageUrl: data.artworkUrl || data.imageUrl,
            source: 'sharedArtworks',
            ...data
          };
        })
      ];
      
      const sortedArtworks = artworksData
        .sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || a.createdAt || 0;
          const bTime = b.createdAt?.toMillis?.() || b.createdAt || 0;
          return bTime - aTime;
        })
        .slice(0, 12);
      
      setUserArtworks(sortedArtworks);
      
      if (sortedArtworks.length > 0 && !imageUrl) {
        setShowArtworkCarousel(true);
      } else if (sortedArtworks.length === 0 && !imageUrl) {
        // No artworks - show upload option
        setShowUploadModal(true);
      }
    } catch (error) {
      console.error('Error loading user artworks:', error);
      // If error, show upload option
      if (!imageUrl) {
        setShowUploadModal(true);
      }
    }
  };
  
  // Handle artwork selection from carousel
  const handleArtworkSelect = (artwork) => {
    const selectedImageUrl = artwork.imageUrl || artwork.artworkUrl;
    const selectedTitle = artwork.title || artwork.artworkTitle || 'My Artwork';
    
    // Update state to show selected artwork
    setShowArtworkCarousel(false);
    
    // Navigate with selected artwork
    navigate('/magic', {
      replace: true,
      state: {
        imageUrl: selectedImageUrl,
        title: selectedTitle,
        technique: artwork.technique || '',
        inspiration: artwork.inspiration || ''
      }
    });
    
    // Force reload by updating location state
    window.location.reload();
  };
  
  // Handle upload new artwork
  const handleUploadNew = () => {
    setShowArtworkCarousel(false);
    setShowUploadModal(false);
    navigate('/artist-wall', { state: { openUpload: true } });
  };

  // Set selectedFeature from location.state if provided (from My Art World)
  useEffect(() => {
    if (passedSelectedFeature) {
      console.log('[AIMagicPage] Setting selectedFeature from location.state:', passedSelectedFeature);
      setSelectedFeature(passedSelectedFeature);
    }
  }, [passedSelectedFeature]);

  // Initialize component and check for mentor artwork
  useEffect(() => {
    console.log('[AIMagicPage] Checking for mentor artwork in localStorage...');
    console.log('[AIMagicPage] Location state:', location.state);
    console.log('[AIMagicPage] Artwork from state:', artwork);
    
    const storedArtwork = localStorage.getItem('mentorSelectedArtwork');
    console.log('[AIMagicPage] Stored artwork found:', !!storedArtwork);
    
    if (storedArtwork) {
      try {
        const artwork = JSON.parse(storedArtwork);
        console.log('[AIMagicPage] Parsed artwork:', artwork);
        setMentorArtwork(artwork);
        // Clear the stored artwork after loading
        localStorage.removeItem('mentorSelectedArtwork');
        
        // Auto-trigger art appreciation for mentor uploads
        if (artwork.feature === 'appreciation') {
          console.log('[AIMagicPage] Routing directly to new ArtAppreciationFeedback layout for mentor upload');
          setSelectedFeature('Art Appreciation');
          navigate('/art-appreciation/feedback', {
            state: { imageUrl: artwork.file, title: artwork.name }
          });
        }
      } catch (error) {
        console.error('[AIMagicPage] Error parsing mentor artwork:', error);
      }
    } else {
      console.log('[AIMagicPage] No mentor artwork found in localStorage');
    }
    
    // For non-mentor users: Load artworks if no imageUrl provided and coming from My Art World
    if (!user?.accountType || user.accountType !== 'mentor') {
      // If artwork is provided from My Art World, we already have it via currentImageUrl
      if (artwork && (artwork.imageUrl || artwork.artworkUrl)) {
        console.log('[AIMagicPage] Artwork provided from My Art World, using it directly');
        // Artwork is already set via currentImageUrl calculation above
      } else if (!imageUrl && fromMyArtWorld && passedUserArtworks && passedUserArtworks.length > 0) {
        // User has artworks - show carousel
        setUserArtworks(passedUserArtworks);
        setShowArtworkCarousel(true);
      } else if (!imageUrl && !passedUserArtworks) {
        // Load artworks to check if user has any
        loadUserArtworks();
      }
    }
    
    setIsInitialized(true);
  }, [user, imageUrl, fromMyArtWorld, passedUserArtworks, artwork, navigate]);

  const handleTechniquesClick = async () => {
    if (!showTechniques && !techniquesInitialized) {
      setTechniquesLoading(true);
      try {
        // If we already have techniques data, use it
        if (techniques.length > 0) {
        setTechniquesInitialized(true);
        } else if (artAnalysis?.techniques && artAnalysis.techniques.length > 0) {
          // Fallback: use techniques from artAnalysis
          setTechniques(artAnalysis.techniques);
        setTechniquesInitialized(true);
        }
      } finally {
        setTechniquesLoading(false);
      }
    }
    setShowTechniques(!showTechniques);
  };

  const fetchStoryScene = async () => {
    if (!imageUrl || !title) {
      setError("Please provide both an image and a title.");
      return;
    }
    setStoryLoading(true);
    setError(null);
    
    try {
      console.log("Sending story scene request:", {
        imageUrl: imageUrl?.substring(0, 100) + "...",
        title,
        technique,
        inspiration
      });

      const response = await fetch(`${API_ENDPOINTS.generateStoryScene}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageUrl,
          title,
          technique,
          inspiration,
          userId: user?.uid // Add user ID for age verification
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Story scene API error:", errorData);
        throw new Error(errorData.error || 'Failed to generate story scene');
      }

      const data = await response.json();
      console.log("Received story scene data:", data);

      if (!data.scene) {
        throw new Error('Invalid response format: missing scene data');
      }

      // Navigate directly to StoryCreator with the generated scene
      console.log('Navigating to /story-creator with generated scene:', data.scene);
      navigate("/story-creator", {
        state: { 
          ...location.state,
          generatedScene: data.scene
        }
      });
    } catch (error) {
      console.error("Story scene error:", error);
      setError(error.message);
    } finally {
      setStoryLoading(false);
    }
  };

  // Removed auto-navigation - user will click "Start Analysis" button to proceed
  // This allows the landing page to show first

  // Add useEffect to handle techniques visibility
  useEffect(() => {
    if (artAnalysis?.techniques) {
      setShowTechniques(true);
    }
  }, [artAnalysis]);

  // Add useEffect to fetch story scene when component mounts
  useEffect(() => {
    if (imageUrl && selectedFeature === "What's Your Story") {
      // Don't automatically navigate
      setStoryLoading(false);
    }
  }, [imageUrl, selectedFeature]);

  const handleMentorQuery = async () => {
    if (!userQuery.trim()) return;

    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.askMentor, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query: userQuery }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to get mentor response");
      }
      
      const data = await response.json();
      setMentorResponse(data.message);
      setUserQuery("");
      // Auto-save Q&A for mentors
      if (user?.accountType === 'mentor') {
        try {
          await saveActivity({
            type: ACTIVITY_TYPES.ASK_MENTOR || 'ask-artmosphere',
            title: 'Ask Artmosphere',
            description: userQuery,
            metadata: {
              feature: 'ask-artmosphere',
              context: 'mentor',
              className: localStorage.getItem('mentorClass') || null,
              section: localStorage.getItem('mentorSection') || null
            },
            originalImageUrl: currentImageUrl
          });
        } catch (_) {}
      }
      // Apply enrichment if present
      try {
        if (Array.isArray(data.points)) {
          setAiPoints(data.points.map(p => ({
            ...p,
            think: Array.isArray(p.think) ? p.think : undefined,
            improve: Array.isArray(p.improve) ? p.improve : undefined
          })));
        }
        if (Array.isArray(data.concepts)) {
          setConcepts(data.concepts);
        }
      } catch (_) {}

    } catch (error) {
      console.error("Error asking mentor:", error);
      setError(error.message || "Failed to get mentor response");
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureSelect = (feature) => {
    setSelectedFeature(feature);
    setError(null);
    
    // Don't auto-navigate for Art Appreciation - show landing page first
    // User will click "Start Analysis" button to proceed
    // Do NOT call fetchStoryScene for "What's Your Story"
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#FFF8E1]">
      {/* Amber color splashes background - matching home page */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-amber-200/25 rounded-full blur-3xl"></div>
        <div className="absolute bottom-40 left-10 w-96 h-96 bg-amber-300/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 right-1/4 w-56 h-56 bg-amber-400/22 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 left-1/3 w-40 h-40 bg-amber-200/15 rounded-full blur-2xl"></div>
        <div className="absolute bottom-1/4 right-1/3 w-48 h-48 bg-amber-300/18 rounded-full blur-3xl"></div>
      </div>
      
      {/* Splash images merged into background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-5">
        <img 
          src={splashImage} 
          alt="" 
          className="absolute top-10 right-5 opacity-20"
        style={{
            width: '400px',
            height: '200px',
            objectFit: 'cover',
            transform: 'rotate(15deg)',
            mixBlendMode: 'multiply',
            filter: 'blur(2px)'
        }}
      />
        <img 
          src={splashImage} 
          alt="" 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-15"
          style={{ 
            width: '300px',
            height: '150px',
            objectFit: 'cover',
            transform: 'translate(-50%, -50%) rotate(10deg)',
            mixBlendMode: 'multiply',
            filter: 'blur(3px)'
          }}
        />
      </div>
      <KidBackground variant="blobs" intensity={1} />



      <div className="relative z-10 p-3 sm:p-6">
        <div className={`flex items-center ${selectedFeature ? 'justify-between' : 'justify-end'} mb-4 sm:mb-6`}>
          {selectedFeature && (
            <button
              onClick={() => {
                // Clear selected feature to go back to main magic page view
                setSelectedFeature(null);
              }}
              className="flex items-center justify-center gap-0 md:gap-2 p-1 md:px-4 md:py-2 bg-amber-400 hover:bg-amber-500 text-white font-bold rounded-lg md:rounded-xl shadow border-2 md:border-4 border-amber-200 transition-all flex-shrink-0"
              title="Back"
            >
              <ArrowLeft className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0 text-white" />
              <span className="hidden md:inline ml-1 text-sm">Back</span>
            </button>
          )}
          <div className="ml-auto flex items-center gap-3">
            {!(user?.accountType === 'mentor' && selectedFeature === 'Art Appreciation' && loading) && (
              <>
                {user?.accountType === 'mentor' ? (
                  <>
                    <button onClick={() => navigate('/mentor-dashboard')} className="bg-amber-400 hover:bg-amber-500 text-white font-bold rounded-xl shadow px-4 py-2 border-4 border-amber-200">Mentor Dashboard</button>
                    <button onClick={() => navigate('/mentor-magic')} className="bg-amber-400 hover:bg-amber-500 text-white font-bold rounded-xl shadow px-4 py-2 border-4 border-amber-200">Magic Studio</button>
                    <button onClick={() => navigate('/mentor-students')} className="bg-amber-400 hover:bg-amber-500 text-white font-bold rounded-xl shadow px-4 py-2 border-4 border-amber-200">My Students</button>
                  </>
                ) : (
                  <>
                    <button title="All activities you do are auto-saved in My Activities" onClick={() => navigate('/activity-dashboard?view=magic-studio', { state: { view: 'magic-studio' } })} className="bg-amber-400 hover:bg-amber-500 text-white font-bold rounded-lg md:rounded-xl shadow px-1.5 py-0.5 md:px-4 md:py-2 border-2 md:border-4 border-amber-200 text-[7px] md:text-base">My Activities</button>
                    <button onClick={() => navigate('/artist-wall')} className="bg-amber-400 hover:bg-amber-500 text-white font-bold rounded-lg md:rounded-xl shadow px-1.5 py-0.5 md:px-4 md:py-2 border-2 md:border-4 border-amber-200 text-[7px] md:text-base">My Gallery</button>
                  </>
                )}
                {isAuthenticated && (
                  <button onClick={async()=>{ try{ await auth.signOut(); }catch(_){}; try{ localStorage.removeItem('mentor_session'); window.dispatchEvent(new Event('mentor_session_changed')); }catch(_){}; navigate('/'); }} className="bg-amber-400 hover:bg-amber-500 text-white font-bold rounded-lg md:rounded-xl shadow px-1.5 py-0.5 md:px-4 md:py-2 border-2 md:border-4 border-amber-200 text-[7px] md:text-base">Sign Out</button>
                )}
              </>
            )}
          </div>
        </div>
        
        {/* Header */}
        {!(user?.accountType === 'mentor' && selectedFeature === 'Art Appreciation' && loading) && (
        <div className="relative mb-4 sm:mb-6">
          <div className="text-center">
            <h1 className="text-2xl sm:text-4xl font-bold text-slate-900 mb-3 sm:mb-4">
              {user?.accountType === 'mentor' 
                ? 'Mentor Magic Studio' 
                : selectedFeature === 'Art Appreciation'
                  ? 'Welcome to Art Appreciation'
                  : selectedFeature === 'Ways of Seeing'
                    ? 'Welcome to Ways of Seeing'
                    : selectedFeature === 'Re-imagine in Different Styles'
                      ? 'Welcome to Re-imagine Studio'
                      : 'Welcome to Artmosphere Magic'}
            </h1>
            {user?.accountType === 'mentor' && (
              <p className="text-slate-700 text-base sm:text-lg">
                Process student artwork with Artmosphere-guided analysis
              </p>
            )}
          </div>
        </div>
        )}
        
        {/* Loading state while initializing */}
        {!isInitialized && (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin h-12 w-12 text-amber-500 mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Loading...</h3>
              <p className="text-white/80">Preparing your magic experience...</p>
            </div>
          </div>
        )}

        {/* Show upload interface for mentors when no artwork is present */}
        {!selectedFeature && isInitialized && user?.accountType === 'mentor' && !currentImageUrl && (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg border-2 border-amber-200">
              <h2 className="text-2xl font-bold text-amber-800 mb-4">No Artwork Selected</h2>
              <p className="text-amber-700 mb-6">Please go to the Mentor Magic Studio to upload and analyze student artwork.</p>
              <button
                onClick={() => navigate('/mentor-magic')}
                className="px-6 py-3 art-btn-primary font-bold rounded-xl shadow-lg transition-colors"
              >
                Go to Mentor Magic Studio
              </button>
            </div>
          </div>
        )}

        {/* Artwork Carousel for non-mentor users without selected artwork */}
        {showArtworkCarousel && isInitialized && !currentImageUrl && user?.accountType !== 'mentor' && userArtworks.length > 0 && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-amber-200"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl md:text-3xl font-bold text-[#1F2937]">Choose Your Artwork</h2>
                  <button
                    onClick={() => {
                      setShowArtworkCarousel(false);
                      setShowUploadModal(true);
                    }}
                    className="text-[#1F2937] hover:text-amber-600 transition-colors"
                  >
                    <FaTimes size={24} />
                  </button>
                </div>
                
                <p className="text-[#1F2937]/70 mb-6">
                  Select one of your artworks to learn from it and explore new creative ideas!
                </p>
                
                {/* Horizontal Scroller Carousel */}
                <div className="overflow-x-auto pb-4 -mx-2 px-2" style={{ scrollbarWidth: 'thin' }}>
                  <div className="flex gap-4">
                    {userArtworks.map((artwork) => {
                      const artworkImageUrl = artwork.imageUrl || artwork.artworkUrl;
                      const artworkTitle = artwork.title || artwork.artworkTitle || 'My Artwork';
                      
                      return (
                        <motion.div
                          key={artwork.id}
                          className="flex-shrink-0 w-48 md:w-64 cursor-pointer group"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleArtworkSelect(artwork)}
                        >
                          <div className="bg-white/75 backdrop-blur-sm rounded-2xl p-3 shadow-lg border border-[rgba(31,41,55,0.08)] hover:shadow-xl transition-all">
                            <div className="aspect-square rounded-xl overflow-hidden mb-3 bg-gradient-to-br from-[#CFE9FF]/70 to-[#E7D9FF]/70">
                              <img
                                src={resolveImageUrl(artworkImageUrl)}
                                alt={artworkTitle}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  const parent = e.target.parentElement;
                                  if (parent && !parent.querySelector('.placeholder')) {
                                    const placeholder = document.createElement('div');
                                    placeholder.className = 'placeholder w-full h-full flex items-center justify-center';
                                    placeholder.innerHTML = '<div class="w-16 h-16 rounded-full bg-[#F7E6A1] opacity-60"></div>';
                                    parent.appendChild(placeholder);
                                  }
                                }}
                              />
                            </div>
                            <h3 className="font-semibold text-[#1F2937] text-sm truncate">{artworkTitle}</h3>
                            {artwork.technique && (
                              <p className="text-xs text-[#1F2937]/60 mt-1 truncate">{artwork.technique}</p>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Upload New Button */}
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={handleUploadNew}
                    className="px-6 py-3 rounded-2xl bg-white/70 text-[#1F2937] font-semibold shadow-sm hover:shadow-md transition-all duration-200 border border-[rgba(31,41,55,0.08)]"
                  >
                    Upload New Artwork
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Upload Modal Prompt for users with no artworks */}
        {showUploadModal && isInitialized && !currentImageUrl && user?.accountType !== 'mentor' && userArtworks.length === 0 && !showArtworkCarousel && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 border border-amber-200"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#F7E6A1] to-[#FFE7B3] flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-[#CFE9FF] opacity-60"></div>
                </div>
                <h2 className="text-2xl font-bold text-[#1F2937] mb-4">Upload Your Artwork</h2>
                <p className="text-[#1F2937]/70 mb-6">
                  Upload your artwork to start learning and exploring with Artmosphere Magic!
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => {
                      setShowUploadModal(false);
                      navigate('/artist-wall', { state: { openUpload: true } });
                    }}
                    className="px-6 py-3 rounded-2xl bg-[#F7E6A1] text-[#1F2937] font-semibold shadow-sm hover:shadow-md transition-all"
                  >
                    Upload artwork
                  </button>
                  <button
                    onClick={() => setShowUploadModal(false)}
                    className="px-6 py-3 rounded-2xl bg-white/70 text-[#1F2937] font-semibold shadow-sm hover:shadow-md transition-all border border-[rgba(31,41,55,0.08)]"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Main Content Layout - Artwork on Left, Features on Right */}
        {/* Only show welcome page if no carousel is showing and user has artwork or is mentor */}
        {(() => {
          console.log('Welcome page condition check:', {
            selectedFeature,
            userAccountType: user?.accountType,
            isInitialized,
            currentImageUrl,
            showArtworkCarousel,
            shouldShowWelcome: !selectedFeature && isInitialized && !showArtworkCarousel && (currentImageUrl || user?.accountType === 'mentor')
          });
          return !selectedFeature && isInitialized && !showArtworkCarousel && (currentImageUrl || (user?.accountType === 'mentor' && currentImageUrl));
        })() && (
          <div className="flex flex-col lg:flex-row gap-4 md:gap-8 items-start justify-center max-w-7xl mx-auto mt-8 px-4 md:px-0">
            {/* Left: Artwork Display */}
            <div className="lg:w-1/2 flex justify-center w-full">
              <div className="relative w-full max-w-full md:max-w-[500px] aspect-square md:h-[500px] rounded-2xl border-2 md:border-4 border-amber-200 shadow-2xl overflow-hidden bg-white/90 backdrop-blur-sm">
                <img
                  src={currentImageUrl}
                  alt={currentTitle || "Your Artwork"}
                  className="w-full h-full object-contain bg-white"
                  className="w-full h-full object-cover"
                />
                {mentorArtwork && (
                  <div className="absolute bottom-4 right-4 text-center text-amber-700 font-bold bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg">
                    <div className="text-sm font-semibold">{mentorArtwork.name}</div>
                    <div className="text-xs opacity-80">by {mentorArtwork.artist}</div>
                  </div>
                )}
                <button
                  className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg hover:bg-white hover:scale-110 transition-all duration-300 z-10"
                  onClick={() => setExpanded(true)}
                  title="Expand"
                >
                  <FaExpandArrowsAlt size={18} className="text-amber-700" />
                </button>
                <div className="absolute bottom-4 left-4 text-center text-amber-700 font-bold bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg">
                  Your Masterpiece
                </div>
              </div>
            </div>
            
            {/* Right: Feature Thumbnails */}
            <div className="lg:w-1/2 flex flex-col justify-start h-[500px]">
              <div className="text-center mb-4">
                <h2 className="text-2xl font-bold text-black mb-2 text-center">
                  {user?.accountType === 'mentor' ? 'Choose Analysis Tool' : 'Choose Your Magic Adventure!'}
                </h2>
                <p className="text-black text-base">
                  {user?.accountType === 'mentor' ? 'Select an Artmosphere tool to analyze your student\'s artwork' : 'What would you like to do with your artwork today?'}
                </p>
              </div>
              
              <div className="flex flex-col gap-3 flex-1">
                {/* Art Appreciation */}
                <div 
                  className="group cursor-pointer relative overflow-hidden bg-gradient-to-br from-yellow-50/90 via-amber-50/90 to-orange-50/90 rounded-xl p-3 shadow-lg hover:shadow-xl hover:scale-105 hover:-rotate-1 transition-all duration-500 border-2 border-amber-200/60 hover:border-amber-300/80 animate-pulse"
                  onClick={() => handleFeatureSelect('Art Appreciation')}
                  style={{
                    backgroundSize: '200% 200%',
                    animation: 'gradientShift 3s ease infinite'
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-100/10 to-amber-100/10 backdrop-blur-sm"></div>
                  <div className="relative text-center">
                    <div className="bg-gradient-to-r from-amber-400/80 to-orange-400/80 rounded-lg p-2 mb-2 shadow-sm">
                      <h3 className="text-base font-bold text-white mb-0 drop-shadow-sm">
                        Art Appreciation
                      </h3>
                    </div>
                    <p className="text-amber-800/80 text-xs leading-tight drop-shadow-sm mb-2">
                      {user?.accountType === 'mentor' ? 'Analyze student artwork with detailed Artmosphere insights and techniques.' : 'Discover the magic in your artwork! Notice details, colors, and creativity.'}
                    </p>
                    <div className="text-amber-600 text-xs font-medium">
                      Click to Explore →
                    </div>
                  </div>
                </div>

                {/* Ways of Seeing */}
                <div 
                  className="group cursor-pointer relative overflow-hidden bg-gradient-to-br from-yellow-50/90 via-amber-50/90 to-orange-50/90 rounded-xl p-3 shadow-lg hover:shadow-xl hover:scale-105 hover:rotate-1 transition-all duration-500 border-2 border-amber-200/60 hover:border-amber-300/80 animate-pulse"
                  onClick={() => navigate('/art-world', { state: { artwork: { imageUrl, originalArtwork: imageUrl, title, technique, inspiration } } })}
                  style={{
                    backgroundSize: '200% 200%',
                    animation: 'gradientShift 3s ease infinite 1s'
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-100/10 to-amber-100/10 backdrop-blur-sm"></div>
                  <div className="relative text-center">
                    <div className="bg-gradient-to-r from-amber-400/80 to-orange-400/80 rounded-lg p-2 mb-2 shadow-sm">
                      <h3 className="text-base font-bold text-white mb-0 drop-shadow-sm">
                        Ways of Seeing
                      </h3>
                    </div>
                    <p className="text-amber-800/80 text-xs leading-tight drop-shadow-sm mb-2">
                      {user?.accountType === 'mentor' ? 'Show students how their art looks in different famous styles.' : 'Transform your artwork with different art styles! See it in Impressionism, Cubism, and more.'}
                    </p>
                    <div className="text-amber-600 text-xs font-medium">
                      Click to Explore →
                    </div>
                  </div>
                </div>

                {/* Re-imagine in Different Styles */}
                <div 
                  className="group cursor-pointer relative overflow-hidden bg-gradient-to-br from-yellow-50/90 via-amber-50/90 to-orange-50/90 rounded-xl p-3 shadow-lg hover:shadow-xl hover:scale-105 hover:-rotate-1 transition-all duration-500 border-2 border-amber-200/60 hover:border-amber-300/80 animate-pulse"
                  onClick={() => handleFeatureSelect('Re-imagine in Different Styles')}
                  style={{
                    backgroundSize: '200% 200%',
                    animation: 'gradientShift 3s ease infinite 2s'
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-100/10 to-amber-100/10 backdrop-blur-sm"></div>
                  <div className="relative text-center">
                    <div className="bg-gradient-to-r from-amber-400/80 to-orange-400/80 rounded-lg p-2 mb-2 shadow-sm">
                      <h3 className="text-base font-bold text-white mb-0 drop-shadow-sm">
                        Re-imagine in Different Styles
                      </h3>
                    </div>
                    <p className="text-amber-800/80 text-xs leading-tight drop-shadow-sm mb-2">
                      {user?.accountType === 'mentor' ? 'Help students explore different art styles and techniques.' : 'Give your artwork a complete makeover! Try famous art styles and see amazing transformations.'}
                    </p>
                    <div className="text-amber-600 text-xs font-medium">
                      Click to Explore →
                    </div>
                  </div>
                </div>

                {/* What's Your Story - HIDDEN */}
                {/* <div 
                  className="group cursor-pointer relative overflow-hidden bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 rounded-2xl p-4 shadow-xl hover:shadow-2xl hover:scale-105 hover:rotate-1 transition-all duration-500 border-3 border-green-300 hover:border-green-200"
                  onClick={() => handleFeatureSelect("What's Your Story")}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-green-200/20 to-emerald-200/20 backdrop-blur-sm"></div>
                  <div className="relative text-center">
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-green-100 transition-colors drop-shadow-lg">
                      What's Your Story
                    </h3>
                    <p className="text-white/90 text-xs leading-relaxed drop-shadow-md">
                      Create an amazing story from your artwork! Let your imagination run wild with magical adventures.
                    </p>
                    <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <span className="inline-block bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full text-white font-semibold text-xs">
                        📚 Story 📚
                      </span>
                    </div>
                  </div>
                </div> */}
              </div>
            </div>
          </div>
        )}

        {/* Removed old inline mentor loading overlay; new layout handles its own loading */}

        {/* Old inline student layout removed: all users now use ArtAppreciationFeedback */}

                    
        {/* What's Your Story - HIDDEN */}
        {/* {selectedFeature === "What's Your Story" && (
          <div className="flex flex-col md:flex-row gap-8 items-start justify-center max-w-7xl mx-auto mt-8">
            {/* Left: Artwork */}
            {/* <div className="relative w-[500px] h-[500px] bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border-2 border-amber-200 overflow-hidden">
              <img
                src={imageUrl}
                alt="Artwork"
                className="w-full h-full object-cover"
              />
              <button
                className="absolute top-3 right-3 bg-white/80 rounded-full p-2 shadow hover:bg-white z-10"
                onClick={() => setExpanded(true)}
                title="Expand"
              >
                <FaExpandArrowsAlt size={16} />
              </button>
              <div className="absolute bottom-2 left-2 text-center text-amber-700 font-semibold bg-white/80 px-2 py-1 rounded">Your Artwork</div>
            </div> */}
            {/* Right: Welcome Message, Features, and Button */}
            {/* <div className="flex-1 space-y-6">
              <div className="bg-amber-50/50 backdrop-blur-sm border border-amber-100/50 rounded-2xl p-6 shadow-lg">
                <h2 className="text-2xl font-bold mb-3 bg-gradient-to-r from-amber-600 to-amber-400 bg-clip-text text-transparent text-center">
                  Welcome to Your Story Adventure!
                </h2>
                <p className="text-amber-800/80 text-center mb-6">
                  Create your own magical story with Artmosphere's story creator. Add characters, create scenes, and watch your imagination come to life!
                </p>
                
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                    <div className="text-red-700 font-semibold">Error: {error}</div>
                    <button 
                      onClick={() => setError(null)}
                      className="mt-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                )}
                
                {/* Feature Thumbnails */}
                {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="bg-gradient-to-br from-amber-50/80 to-amber-100/50 backdrop-blur-sm border border-amber-100/50 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <h3 className="text-lg font-semibold mb-2 text-amber-800">Meet Magical Characters</h3>
                    <p className="text-sm text-amber-700/80">Create and customize your own characters with unique personalities and traits.</p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-50/80 to-amber-100/50 backdrop-blur-sm border border-amber-100/50 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <h3 className="text-lg font-semibold mb-2 text-amber-800">Create Amazing Scenes</h3>
                    <p className="text-sm text-amber-700/80">Design beautiful scenes with Artmosphere's image generator.</p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-50/80 to-amber-100/50 backdrop-blur-sm border border-amber-100/50 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <h3 className="text-lg font-semibold mb-2 text-amber-800">Add Special Magic</h3>
                    <p className="text-sm text-amber-700/80">Enhance your story with magical elements and special effects.</p>
                  </div>
                </div> */}
                
                {/* Action Button */}
                {/* <div className="flex justify-center mt-8">
                  {!storyLoading && (
                    <button
                      onClick={() => navigate("/story-creator", { state: { imageUrl, title, technique, inspiration } })}
                      className="art-btn-primary px-8 py-4 text-xl rounded-full shadow-lg transition-all duration-300 font-bold"
                      style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}
                    >
                      Start Your Story Adventure! ✨
                    </button>
                  )}
                </div> */}
              {/* </div> */}
            {/* </div> */}
          {/* </div> */}
        {/* )} */}         
        
        {/* Art Appreciation Landing Page */}
        {selectedFeature === "Art Appreciation" && !loading && (
          <div className="flex flex-col md:flex-row gap-4 md:gap-8 items-start justify-center max-w-7xl mx-auto mt-8 px-4 md:px-0">
            {/* Left: Artwork */}
            <div className="relative w-full max-w-full md:w-[500px] aspect-square md:h-[500px] bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border-2 border-amber-200 overflow-hidden mx-auto md:mx-0">
              <img
                src={currentImageUrl}
                alt={currentTitle || "Your Artwork"}
                className="w-full h-full object-contain bg-white"
                onLoad={() => setImageLoaded(true)}
              />
              <div className="absolute bottom-2 left-2 text-center text-amber-700 font-semibold bg-white/80 px-2 py-1 rounded">Your Artwork</div>
            </div>
            {/* Right: Welcome and Features */}
            <div className="flex-1 space-y-6">
              <div className="bg-amber-50/50 backdrop-blur-sm border border-amber-100/50 rounded-2xl p-6 shadow-lg">
                <h2 className="text-2xl font-bold mb-3 bg-gradient-to-r from-amber-600 to-amber-400 bg-clip-text text-transparent text-center">
                  Welcome to Art Appreciation
                </h2>
                <p className="text-amber-800/80 text-center mb-6">
                  Discover the magic in your artwork! Artmosphere will analyze your creation and reveal amazing details about colors, composition, techniques, and creative elements.
                </p>
                {/* Feature Highlights */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  <div className="bg-gradient-to-br from-amber-50/80 to-amber-100/50 backdrop-blur-sm border border-amber-100/50 rounded-xl p-4 shadow-lg">
                    <div className="font-bold text-amber-800 text-lg mb-1">Key Insights</div>
                    <div className="text-amber-700 text-sm">Discover what makes your artwork special with detailed analysis points</div>
                  </div>
                  <div className="bg-gradient-to-br from-amber-50/80 to-amber-100/50 backdrop-blur-sm border border-amber-100/50 rounded-xl p-4 shadow-lg">
                    <div className="font-bold text-amber-800 text-lg mb-1">Art Techniques</div>
                    <div className="text-amber-700 text-sm">Learn about the techniques and styles used in your creation</div>
                  </div>
                  <div className="bg-gradient-to-br from-amber-50/80 to-amber-100/50 backdrop-blur-sm border border-amber-100/50 rounded-xl p-4 shadow-lg">
                    <div className="font-bold text-amber-800 text-lg mb-1">Creative Concepts</div>
                    <div className="text-amber-700 text-sm">Explore interesting concepts and ideas related to your artwork</div>
                  </div>
                  <div className="bg-gradient-to-br from-amber-50/80 to-amber-100/50 backdrop-blur-sm border border-amber-100/50 rounded-xl p-4 shadow-lg">
                    <div className="font-bold text-amber-800 text-lg mb-1">Ask Artmosphere</div>
                    <div className="text-amber-700 text-sm">Get answers to your questions about your artwork</div>
                  </div>
                </div>
                {/* Start Analysis Button */}
                <div className="flex justify-center mt-8">
                  <button
                    onClick={() => {
                      navigate('/art-appreciation/feedback', {
                        state: { 
                          imageUrl: currentImageUrl, 
                          title: currentTitle, 
                          technique, 
                          inspiration,
                          startAnalysis: true // Flag to indicate user explicitly started analysis
                        }
                      });
                    }}
                    className="art-btn-primary px-8 py-4 text-xl rounded-full shadow-lg transition-all duration-300 font-bold"
                  >
                    Start Analysis
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {selectedFeature === 'Ways of Seeing' && (
          <div className="flex flex-col md:flex-row gap-4 md:gap-8 items-start justify-center max-w-7xl mx-auto mt-8 px-4 md:px-0">
            {/* Left: Artwork */}
            <div className="relative w-full max-w-full md:w-[500px] aspect-square md:h-[500px] bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border-2 border-amber-200 overflow-hidden mx-auto md:mx-0">
              <img
                src={imageUrl}
                alt="Artwork"
                className="w-full h-full object-contain bg-white"
              />
              <div className="absolute bottom-2 left-2 text-center text-amber-700 font-semibold bg-white/80 px-2 py-1 rounded text-xs md:text-sm">Your Artwork</div>
            </div>
            {/* Right: Welcome and Features in a styled box (no artwork image here) */}
            <div className="flex-1 space-y-6">
              <div className="bg-amber-50/50 backdrop-blur-sm border border-amber-100/50 rounded-2xl p-6 shadow-lg">
                <h2 className="text-2xl font-bold mb-3 bg-gradient-to-r from-amber-600 to-amber-400 bg-clip-text text-transparent text-center">
                  Welcome to Ways of Seeing!
                </h2>
                <p className="text-amber-800/80 text-center mb-6">
                  Give your artwork a magical twist! Add new colors, characters, and styles. Learn about famous art movements and see your art transform—side by side!
                </p>
                {/* Feature Thumbnails */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  {reimagineFeatures.map((f, i) => (
                    <div key={i} className="bg-gradient-to-br from-amber-50/80 to-amber-100/50 backdrop-blur-sm border border-amber-100/50 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div>
                        <div className="font-bold text-amber-800 text-lg mb-1">{f.title}</div>
                        <div className="text-amber-700 text-base">{f.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Nudge Button */}
                <div className="flex justify-center mt-8">
                  <button
                    className="art-btn-primary px-10 py-5 text-2xl rounded-full shadow-lg transition-all duration-300 font-bold animate-pulse"
                    style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}
                    onClick={() => {
                      // Auto-save entry to mentor activities
                      if (user?.accountType === 'mentor') {
                        saveActivity({
                          type: ACTIVITY_TYPES.WAYS_OF_SEEING || 'ways-of-seeing',
                          title: 'Ways of Seeing - Started',
                          description: `Mentor started Ways of Seeing for ${currentTitle || 'artwork'}`,
                          metadata: {
                            feature: 'ways-of-seeing',
                            context: 'mentor',
                            className: localStorage.getItem('mentorClass') || null,
                            section: localStorage.getItem('mentorSection') || null
                          },
                          originalImageUrl: imageUrl
                        }).catch(()=>{});
                      }
                      navigate('/art-world', { state: { artwork: { imageUrl, originalArtwork: imageUrl, title, technique, inspiration } } })
                    }}
                  >
                    Enter the World of Art
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {selectedFeature === 'Re-imagine in Different Styles' && (
          <div className="flex flex-col md:flex-row gap-4 md:gap-8 items-start justify-center max-w-7xl mx-auto mt-8 px-4 md:px-0">
            {/* Left: Artwork */}
            <div className="relative w-full max-w-full md:w-[500px] aspect-square md:h-[500px] bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border-2 border-amber-200 overflow-hidden mx-auto md:mx-0">
              <img
                src={imageUrl}
                alt="Artwork"
                className="w-full h-full object-contain bg-white"
              />
              <div className="absolute bottom-2 left-2 text-center text-amber-700 font-semibold bg-white/80 px-2 py-1 rounded">Your Artwork</div>
            </div>
            {/* Right: Welcome and Features in a styled box */}
            <div className="flex-1 space-y-6">
              <div className="bg-amber-50/50 backdrop-blur-sm border border-amber-100/50 rounded-2xl p-6 shadow-lg">
                <h2 className="text-2xl font-bold mb-3 text-black text-center">
                  Welcome to Re-imagine in Different Styles!
                </h2>
                <p className="text-black text-center mb-6">
                  Transform your artwork with famous art styles! See your creation in Impressionism, Cubism, Surrealism, and more. Each style will give your artwork a completely new look and feel!
                </p>
                {/* Feature Thumbnails */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="bg-gradient-to-br from-amber-50/80 to-orange-100/50 backdrop-blur-sm border border-amber-100/50 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div>
                      <div className="font-bold text-amber-800 text-lg mb-1">Art Style Gallery</div>
                      <div className="text-amber-700 text-base">Choose from famous art movements like Impressionism, Cubism, and more!</div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-amber-50/80 to-orange-100/50 backdrop-blur-sm border border-amber-100/50 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div>
                      <div className="font-bold text-amber-800 text-lg mb-1">Instant Transformation</div>
                      <div className="text-amber-700 text-base">Watch your artwork transform instantly!</div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-amber-50/80 to-orange-100/50 backdrop-blur-sm border border-amber-100/50 rounded-xl p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div>
                      <div className="font-bold text-amber-800 text-lg mb-1">Learn & Create</div>
                      <div className="text-amber-700 text-base">Learn about each art style and create your own versions!</div>
                    </div>
                  </div>
                </div>
                {/* Action Button */}
                <div className="flex justify-center mt-8">
                  <button
                    className="art-btn-primary px-10 py-5 text-2xl rounded-full shadow-lg transition-all duration-300 font-bold animate-pulse"
                    style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}
                    onClick={() => {
                      if (user?.accountType === 'mentor') {
                        saveActivity({
                          type: ACTIVITY_TYPES.REIMAGINE || 'reimagine',
                          title: 'Reimagine - Started',
                          description: `Mentor started Reimagine for ${currentTitle || 'artwork'}`,
                          metadata: {
                            feature: 'reimagine-artwork',
                            context: 'mentor',
                            className: localStorage.getItem('mentorClass') || null,
                            section: localStorage.getItem('mentorSection') || null
                          },
                          originalImageUrl: imageUrl
                        }).catch(()=>{});
                      }
                      navigate('/reimagine-studio', { state: { artwork: { imageUrl, originalArtwork: imageUrl, title, technique, inspiration } } })
                    }}
                  >
                    Start Reimagining
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Story Loading Overlay */}
      {storyLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
            <div className="text-center">
              <div className="animate-spin h-12 w-12 text-amber-500 mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-amber-800 mb-2">Creating Your Story Adventure!</h3>
              <p className="text-amber-700">Generating your magical first scene...</p>
            </div>
          </div>
        </div>
      )}

      {/* Look Here Popup */}
      {showLocationPopup && selectedLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-amber-700">Look Here!</h3>
              <button
                onClick={() => setShowLocationPopup(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Where Technique is Used */}
              <div className="bg-amber-50 p-4 rounded-lg">
                <h4 className="font-bold text-amber-800 mb-2">Where You Used This Technique</h4>
                <p className="text-gray-700">{selectedLocation.location.replace('Look Here: ', '')}</p>
              </div>
              
              {/* How to Spot It */}
              <div className="bg-amber-50 p-4 rounded-lg">
                <h4 className="font-bold text-amber-800 mb-2">How to Spot It</h4>
                <p className="text-gray-700">{selectedLocation.explanation}</p>
              </div>

              {/* Famous Artist */}
              {selectedLocation.example && (
                <div className="bg-amber-50 p-4 rounded-lg">
                  <h4 className="font-bold text-amber-800 mb-2">Famous Artist</h4>
                  <p className="text-gray-700">Did you know? Even famous artists like {selectedLocation.example} have used this technique in their artworks!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Expanded Artwork Modal */}
      {expanded && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-4">
          <button
            className="absolute top-6 right-6 bg-white/90 rounded-full p-3 shadow hover:bg-white z-60"
            onClick={() => setExpanded(false)}
            title="Close"
          >
            <FaTimes size={28} />
          </button>
          <div className="relative max-w-full max-h-[90vh] rounded-2xl border-4 border-amber-200 shadow-2xl overflow-hidden">
            <img
              src={currentImageUrl}
              alt={currentTitle || "Expanded Artwork"}
              className="max-w-full max-h-[90vh] object-contain"
              onLoad={() => setImageLoaded(true)}
              style={{ background: 'white' }}
            />
            
            {/* Art Appreciation Points on Expanded Artwork */}
            {selectedFeature === "Art Appreciation" && !loading && imageLoaded && aiPoints.map((point, index) => {
              if (!point || !point.point || typeof point.point.x !== 'number' || typeof point.point.y !== 'number') {
                return null;
              }

              // Ensure coordinates are within bounds (0.1 to 0.9)
              const x = Math.min(Math.max(point.point.x, 0.1), 0.9);
              const y = Math.min(Math.max(point.point.y, 0.1), 0.9);

              return (
                <div
                  key={index}
                  className="absolute w-12 h-12 flex items-center justify-center rounded-full font-bold text-white text-lg bg-gradient-to-br from-amber-500 to-amber-400 shadow-xl ring-2 ring-white animate-pulse cursor-pointer hover:scale-125 hover:ring-4 transition-all duration-300 z-10"
                  style={{
                    top: `${y * 100}%`,
                    left: `${x * 100}%`,
                    transform: "translate(-50%, -50%)"
                  }}
                  onClick={() => setVisibleBoxIndex(index)}
                  title="Click to discover something magical!"
                >
                  {index + 1}
                </div>
              );
            })}

            {/* Appreciation popup for selected circle in expanded view with Think/Improve */}
            {visibleBoxIndex !== null && (
              <div className="absolute top-4 left-4 right-4 bg-white/95 backdrop-blur-sm border-2 border-amber-200 rounded-2xl p-4 text-sm text-gray-800 shadow-xl z-50">
                <div className="flex justify-between items-center">
                  <strong className="text-amber-700">{aiPoints[visibleBoxIndex]?.label}</strong>
                  <button onClick={() => setVisibleBoxIndex(null)} className="text-amber-600 hover:text-amber-800 transition-colors">✖</button>
                </div>
                <p className="mt-2 text-gray-700">{aiPoints[visibleBoxIndex]?.text}</p>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="rounded-xl border border-amber-200 p-3 bg-amber-50/60">
                    <div className="font-semibold text-amber-800 mb-1">Think</div>
                    <p className="text-amber-700/90 text-xs mb-1">Above is Artmosphere’s view. As the artist, what do YOU think about the questions below?</p>
                    <ul className="list-disc ml-5 text-amber-800/90 space-y-1">
                      {(aiPoints[visibleBoxIndex]?.think || [
                        'What feeling do darker shades of blue create? Could a lighter shade change the mood?',
                        'Where does your eye go first? Why do you think that is?',
                      ]).map((t, i) => (<li key={`think-${i}`}>{t}</li>))}
                    </ul>
                  </div>
                  <div className="rounded-xl border border-amber-200 p-3 bg-amber-50/60">
                    <div className="font-semibold text-amber-800 mb-1">Improve</div>
                    <ul className="list-disc ml-5 text-amber-800/90 space-y-1">
                      {(aiPoints[visibleBoxIndex]?.improve || [
                        'Try mixing a little white or yellow to see how brightness changes the emotion.',
                        'Experiment with softer edges vs. sharper lines to change the focus.',
                      ]).map((t, i) => (<li key={`improve-${i}`}>{t}</li>))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AIMagicPage;
